import { duelDisplayedHp, duelDisplayedFervor, duelTiming } from './duelPresentation';
import {terminalTiming} from './duelTerminal';
import type { ArenaFact, Screen, ScreenContext, ScreenDef, ScreenParams } from '../../shared';
import { h, disposeTree } from '../../ui';
import { ACTIONS, OPPONENTS, TECHNIQUES, isTechniqueAction, canPlay, createDuel, playTurn, rivalState, maxStamina,type DuelState, type OpponentId, type PlayerAction } from '../sim';
import { peakCounterCombo, readCombat, type CombatSave } from '../save';
import { createArenaArt } from './arenaArt';
import { rivalPortraitSvg } from './rivalPortrait';
import { duelFeedback } from './duelFeedback';
import { combatStyles } from './styles';
import { actionSealSvg, artElement, crestSvg, sunSvg, vineSvg } from './manuscriptArt';
import './manuscript.css';

const foeIds: OpponentId[] = ['novice', 'knight', 'prior', 'collector'];
const saveOf = (ctx: ScreenContext): CombatSave => readCombat(ctx.store.state.slices.combat);
const label = (ctx: ScreenContext, key: string, values?: Record<string, string | number>): string => ctx.t('combat.' + key, values);
function button(ctx: ScreenContext, text: string, fn: () => void, primary = false): HTMLButtonElement {
  return h('button', { class: 'duel-button' + (primary ? ' primary' : ''), attrs: { type: 'button' }, on: { click: fn } }, label(ctx, text));
}
function shell(ctx: ScreenContext, title = 'screen.arena'): HTMLElement {
  combatStyles();
  return h('section', { class: 'duel-page', attrs: { 'data-combat-screen': title } },
    h('div', { class: 'duel-kicker' }, label(ctx, 'kicker')),
    h('h1', null, label(ctx, title)));
}
function simple(root: HTMLElement): Screen { return { root, isIdle: () => true, dispose: () => disposeTree(root) }; }

function startDuel(ctx: ScreenContext, opponent: OpponentId): void {
  const saved = saveOf(ctx), hero = ctx.services.creatures.active();
  const serial = saved.serial + 1;
  const id = 'duel-' + serial;
  const state = createDuel({ seed: ctx.store.state.seed + ':' + id, opponent,
    level: ctx.services.economy.level(hero?.id ?? 'hero'),
    might: ctx.services.economy.balance('training.might'),
    grit: ctx.services.economy.balance('training.grit'),
    cunning: ctx.services.economy.balance('training.cunning'),build:ctx.services.equipment?.current() });
  ctx.store.update('combat', d => Object.assign(d, saved, { serial, active: { id, state } }), 'combat.start');
  ctx.bus.emit('combat/fightStarted', { fightId: id, mode: 'arena' });
  void ctx.router.goto('combat/fight', { fightId: id });
}

function arena(ctx: ScreenContext): Screen {
  const root = shell(ctx), saved = saveOf(ctx);
  root.append(h('p', { class: 'duel-lead' }, label(ctx, 'lead')));
  const track = h('div', { class: 'duel-ladder', attrs: { 'aria-label': label(ctx, 'rank', { n: Math.min(12, saved.wins) }) } },
    ...Array.from({ length: 12 }, (_, i) => h('span', { class: 'duel-rung' + (i < saved.wins ? ' is-won' : '') })));
  root.append(h('div', { class: 'duel-stats' }, h('span', null, label(ctx, 'record', { wins: saved.wins, losses: saved.losses })), track,
    h('span', null, label(ctx, 'rank', { n: Math.min(12, saved.wins) }))));
  if (saved.active?.state.winner === null) root.append(button(ctx, 'resume', () => { void ctx.router.goto('combat/fight', { fightId: saved.active!.id }); }, true));
  root.append(h('h2', null, label(ctx, 'choose')));
  const cards = h('div', { class: 'duel-rivals' });
  foeIds.forEach((id, i) => {
    const foe = OPPONENTS[id], art = h('div', { class: 'duel-rival-art' });
    art.innerHTML = rivalPortraitSvg(id);
    cards.append(h('article', { class: 'duel-rival', attrs:{'data-rival':id} }, art,
      h('div', { class: 'duel-tag' }, h('span', null, label(ctx, 'difficulty.' + id)), h('span', null, label(ctx, 'reward', { n: foe.bounty }))),
      h('h2', null, label(ctx, 'foe.' + id)), h('p', null, label(ctx, 'flavor.' + id)),
      h('small', null, label(ctx, 'habit', { action: label(ctx, 'action.' + foe.habit) })),
      h('p', { class: 'duel-rival-rule' }, label(ctx, 'mechanic.' + id)),
      button(ctx, 'duel', () => startDuel(ctx, id), i === 1)));
  });
  root.append(cards, h('div', { class: 'duel-actions' },
    button(ctx, 'rules', () => { void ctx.router.goto('combat/instruction'); }),
    button(ctx, 'screen.ladder', () => { void ctx.router.goto('combat/ladder'); })));
  return simple(root);
}

function settle(ctx: ScreenContext, id: string, state: DuelState): void {
  if (state.winner === null || saveOf(ctx).history.some(row => row.id === id)) return;
  const won = state.winner === 0, opponent = OPPONENTS[state.spec.opponent];
  const fact: ArenaFact = {
    mode: 'arena', outcome: won ? 'win' : 'loss', bounty: opponent.bounty,
    bountyBand: state.spec.opponent === 'novice' ? 'low' : state.spec.opponent === 'knight' ? 'par' : 'high',
    byName: false, vow: null, vigilMet: true, attended: true,
    closeLoss: !won && state.foeHp <= state.foeMaxHp * 0.25,
    initials: Math.min(3, state.counters), beauty: Math.min(3, state.counters) as 0 | 1 | 2 | 3,
    perfectOmens: 0, oppLevel: state.spec.level,
  };
  ctx.store.transaction('combat.finish', () => {
    const grant = ctx.services.rewards.grant(won ? 'arena.win' : 'arena.loss', { arena: fact, params: { claimId: id } });
    ctx.store.update('combat', d => {
      const saved = readCombat(d);
      Object.assign(d, saved, {
        wins: saved.wins + (won ? 1 : 0), losses: saved.losses + (won ? 0 : 1),
        bestCombo: Math.max(saved.bestCombo, state.combo, peakCounterCombo(state.beats)),
        active: { id, state, receipt: grant.lines },
        history: [{ id, opponent: state.spec.opponent, won, rounds: state.beats.length, counters: state.counters }, ...saved.history].slice(0, 30),
      });
    }, 'combat.record');
  });
  ctx.bus.emit('combat/fightEnded', { fightId: id, mode: 'arena', winner: state.winner, reason: state.hp === 0 || state.foeHp === 0 ? 'ko' : 'judges' });
}

function resultCard(ctx: ScreenContext, state: DuelState): HTMLElement {
  const won = state.winner === 0;
  const receipt = saveOf(ctx).active?.receipt;
  return h('section', { class: 'duel-result', attrs: { 'aria-live': 'polite' } },
    h('h2', null, label(ctx, won ? 'win' : 'loss')),
    h('p', null, label(ctx, won ? 'winSub' : 'lossSub')),
    h('p', null, label(ctx, 'stats', { rounds: state.beats.length, counters: state.counters })),
    h('p', { class: 'duel-receipt' }, receipt?.length ? receipt.map(line => ctx.t(line.key, line.params)).join(' · ') : label(ctx, 'paid')),
    h('div', { class: 'duel-actions' },
      button(ctx, 'again', () => { void ctx.router.goto('combat/arena'); }, true),
      button(ctx, 'upgrade', () => { void ctx.router.goto('meta/life'); })));
}

function fight(ctx: ScreenContext, params: ScreenParams): Screen {
  const active = saveOf(ctx).active;
  if (!active || (params.fightId && params.fightId !== active.id)) {
    const root = shell(ctx, 'screen.fight');
    root.append(h('p', null, label(ctx, 'noDuel')), button(ctx, 'back', () => { void ctx.router.goto('combat/arena'); }, true));
    return simple(root);
  }
  combatStyles();
  let state = active.state, busyUntil = 0, actionAt=0, disposed = false;
  let beforeHp:[number,number]=[state.hp,state.foeHp],lastVisualPhase=-1;
  const id = active.id;
  const root = h('section', { class: 'duel-page duel-fight duel-manuscript', attrs: { 'data-combat-screen': 'fight' } });
  const round = h('h1', null, '');
  const top = h('div', { class: 'duel-top' }, button(ctx, 'back', () => { void ctx.router.goto('combat/arena'); }), round);
  const heroName = ctx.services.creatures.active()?.dna.name || label(ctx, 'you');
  const bars = [heroName, label(ctx, 'foe.' + state.spec.opponent)].map((name, i) => {
    const fill = h('span', { class: 'duel-hp-fill' }), number = h('small', null, '');
    const crest = i === 0 ? 'fish' : state.spec.opponent === 'novice' ? 'hare' : state.spec.opponent === 'prior' ? 'prior' : state.spec.opponent === 'collector' ? 'fox' : 'cat';
    const bar = h('div', { class: 'duel-hp' + (i === 1 ? ' enemy' : '') }, artElement('duel-crest', crestSvg(crest)), h('span', { class: 'duel-hp-name', attrs: { title: name } }, name),
      h('span', { class: 'duel-hp-track', attrs: { role: 'progressbar', 'aria-label': label(ctx, 'health') + ': ' + name, 'aria-valuemin': 0 } }, fill), number);
    return { bar, fill, number };
  });
  const stage = h('div', { class: 'duel-stage', attrs: { role: 'img', 'aria-label': heroName + ' · ' + label(ctx, 'foe.' + state.spec.opponent) } });
  const picture = h('div', { class: 'duel-picture' }, stage, artElement('duel-foliage left', vineSvg()), artElement('duel-foliage right', vineSvg(true)));
  const intent = h('div', { class: 'duel-intent' });
  const stamina = h('span'), fervor = h('span'), combo = h('span');
  const log = h('div', { class: 'duel-log', attrs: { 'aria-live': 'polite', 'aria-atomic': true } });
  const joke = h('div', { class: 'duel-joke' });
  const moves = h('div', { class: 'duel-moves' }), magic = h('div', { class: 'duel-magic' });
  const buttons = new Map<PlayerAction, HTMLButtonElement>();
  const abilities=state.spec.build?[...state.spec.build.abilities].sort((a,b)=>Number(isTechniqueAction(b))-Number(isTechniqueAction(a))):['signature' as const];
  const actions: PlayerAction[] = [...ACTIONS, 'prayer', ...abilities];
  actions.forEach((action, i) => {
    const technique=isTechniqueAction(action)?TECHNIQUES[action]:null;
    const title = h('span', { class: 'duel-action-name' }, label(ctx, 'action.' + action));
    const face = i < 3 ? h('span', { class: 'duel-seal' }, artElement('duel-seal-art', actionSealSvg(action as 'strike' | 'guard' | 'feint')), title) : title;
    const b = h('button', { class: 'duel-button' + (i < 3 ? ' duel-move' : ''), attrs: { type: 'button', 'data-action': action,'data-ability-tier':i<3?'basic':action==='prayer'?'prayer':technique?'technique':'ultimate' },
      on: { click: () => act(action) } }, face,
      ...(technique?[h('small',{class:'duel-ability-cost'},label(ctx,technique.stamina?'cost.technique':'cost.fervor',technique))]:[]),
      h('small', null, label(ctx, 'hint.' + action,{heal:Math.round(state.maxHp*.32)+(state.spec.build?.effects.prayerBonus??0)})));
    buttons.set(action, b); (i < 3 ? moves : magic).append(b);
  });
  const skillCount = h('span', { class: 'duel-skill-count' });
  const arts = h('details', { class: 'duel-arts' }, h('summary', null, h('span', null, label(ctx, 'arts')), skillCount), magic);
  const controls = h('div', { class: 'duel-controls' }, intent, h('div', { class: 'duel-resources' }, stamina, fervor, combo), moves, arts, log, joke);
  if(state.spec.opponent==='collector')controls.append(h('details',{class:'duel-levy-rule'},h('summary',null,label(ctx,'levyDetails')),h('p',null,label(ctx,'levyRule'))));
  if(state.spec.build?.abilities.length===1)magic.after(h('p',{class:'duel-rival-rule',attrs:{'data-frozen-technique-notice':''}},label(ctx,'nextDuelTechnique')));
  const result = h('div');
  const colophon = h('div', { class: 'duel-colophon', attrs: { lang: 'la', 'aria-hidden': 'true' } }, '· MUNDUS RIDET · SED PUGNAMUS ·');
  root.append(top, h('div', { class: 'duel-hud' }, bars[0].bar, artElement('duel-sun', sunSvg()), bars[1].bar), picture, controls, result, colophon);
  const art = createArenaArt(ctx, stage, state.spec.opponent,state.winner,state.spec.build);
  const feedback=duelFeedback(ctx);

  function refresh(): void {
    const beat=state.beats.at(-1),playing=!!busyUntil&&ctx.clock.presentMs<busyUntil;
    root.dataset.busy = String(playing);
    const timing=beat?duelTiming(beat):null,elapsed=playing&&timing?ctx.clock.presentMs-actionAt:Infinity;
    const heroDone=!playing||!!timing&&elapsed>=timing.heroContact.at(-1)!;
    const enemyDone=!playing||!!timing&&elapsed>=timing.enemyContact;
    const [hp,foeHp]=playing&&beat?duelDisplayedHp(beat,beforeHp,[state.maxHp,state.foeMaxHp],elapsed):[state.hp,state.foeHp];
    round.textContent = label(ctx, 'round', { n: Math.min(24, state.winner!==null?state.beats.length:playing?beat!.round:state.round) });
    const values = [[hp, state.maxHp], [foeHp, state.foeMaxHp]];
    bars.forEach((b, i) => {
      const [hp, max] = values[i]; b.fill.style.width = hp / max * 100 + '%'; b.number.textContent = hp + ' / ' + max;
      b.fill.parentElement?.setAttribute('aria-valuenow', String(hp));
      b.fill.parentElement?.setAttribute('aria-valuemax', String(max));
    });
    const rival=rivalState(state),phase=playing?beat!.rivalPhase??'normal':rival.phase;
    if(!playing)art.setIntent(state.intent,phase);
    const telegraph=playing?beat!.intent:state.intent,heavy=playing?beat!.heavy:state.heavy;
    const armour=playing?(beat!.armourRemaining??rival.armour)+(!heroDone&&beat!.armourBroken?1:0):rival.armour;
    intent.textContent = phase!=='normal' ? label(ctx,'phase.'+phase,{n:armour,block:armour*4,action:label(ctx,'action.'+telegraph)}) : heavy ? label(ctx, 'heavy') : label(ctx, 'intent', { action: label(ctx, 'action.' + telegraph) });
    intent.classList.toggle('is-heavy', heavy);
    stamina.textContent = label(ctx, 'stamina', { n: state.stamina,max:maxStamina(state) });
    fervor.textContent = label(ctx, 'fervor', { n: duelDisplayedFervor(beat,state.fervor,elapsed) });
    combo.textContent = state.combo > 1 ? label(ctx, 'combo', { n: state.combo }) : '';
    log.textContent = playing&&!enemyDone ? label(ctx,'exchange') : beat ? (beat.counter ? label(ctx, 'counter') + ' ' : '') + label(ctx, 'hit', { dealt: beat.dealt, received: beat.received }) + (beat.healed ? ' · ' + label(ctx, 'heal', { n: beat.healed }) : '') : label(ctx, 'wait');
    if(beat&&(!playing||enemyDone)&&beat.rushStopped)log.textContent+=' · '+label(ctx,'event.rushStopped');
    if(beat&&(!playing||enemyDone)&&beat.armourBroken)log.textContent+=' · '+label(ctx,'event.armourBroken',{n:beat.armourRemaining??0});
    if(beat&&(!playing||enemyDone)&&beat.armourBlocked)log.textContent+=' · '+label(ctx,'event.armourBlocked',{n:beat.armourBlocked});
    if(beat&&(!playing||enemyDone)&&beat.interrupted)log.textContent+=' · '+label(ctx,'event.interrupted');
    if(beat&&(!playing||enemyDone)&&beat.foeHealed)log.textContent+=' · '+label(ctx,'event.foeHealed',{n:beat.foeHealed});
    if(beat&&(!playing||enemyDone)&&beat.shieldBlocked)log.textContent+=' · '+label(ctx,'event.shieldBlocked',{n:beat.shieldBlocked});
    if(beat&&(!playing||enemyDone)&&beat.techniqueBlocked)log.textContent+=' · '+label(ctx,'event.techniqueBlocked',{n:beat.techniqueBlocked});
    if(beat&&(!playing||enemyDone)&&beat.staminaRestored)log.textContent+=' · '+label(ctx,'event.staminaRestored',{n:beat.staminaRestored});
    if(beat&&(!playing||enemyDone)&&beat.fervorTaken)log.textContent+=' · '+label(ctx,'event.fervorTaken',{n:beat.fervorTaken});
    if(beat&&(!playing||enemyDone)&&beat.levyEvaded)log.textContent+=' · '+label(ctx,'event.levyEvaded');
    if(beat&&(!playing||enemyDone)&&beat.levySuppressed)log.textContent+=' · '+label(ctx,'event.levySuppressed');
    joke.textContent = beat ? label(ctx, 'joke.' + beat.joke) : label(ctx, 'flavor.' + state.spec.opponent);
    const concluded=state.winner!==null&&(!playing||!!beat&&elapsed>=terminalTiming(beat,state.winner).contact);
    controls.classList.toggle('is-concluded',concluded);
    if(concluded){intent.textContent=label(ctx,state.winner===0?'win':'loss');intent.classList.remove('is-heavy');log.textContent=label(ctx,state.winner===0?'winSub':'lossSub');}
    for (const [action, b] of buttons) b.disabled = art.loading || !canPlay(state, action) || ctx.clock.presentMs < busyUntil;
    const available = actions.slice(3).filter(action => canPlay(state, action)).length;
    skillCount.textContent = label(ctx, 'artsReady', { n: available });
    arts.classList.toggle('has-ready', available > 0 && !playing && state.winner === null);
    if (state.winner !== null && !playing) {
      controls.hidden = true;
      if (!result.childElementCount) result.append(resultCard(ctx, state));
    }
  }
  function act(action: PlayerAction): void {
    if (disposed || art.loading || ctx.clock.presentMs < busyUntil) return;
    const next = playTurn(state, action);
    if (next === state) return;
    beforeHp=[state.hp,state.foeHp];state = next;actionAt=ctx.clock.presentMs;
    const latest=state.beats.at(-1)!,reduced=/^(min|reduced)$/.test(document.documentElement.dataset.motion??'');
    // A skill may be chosen below the fold at enlarged text. Bring its actual
    // performance into view before the first animation frame, without resizing it.
    const view = root.closest<HTMLElement>('.mc-screen--base');
    const field = stage.getBoundingClientRect();
    if (view && (field.top < 8 || field.bottom > innerHeight - 8)) {
      view.scrollBy({ top: field.top - Math.max(8, (innerHeight - field.height) * .38), behavior: 'instant' });
    }
    busyUntil=actionAt+(state.winner===null?duelTiming(latest).duration:terminalTiming(latest,state.winner,reduced).resultAt);lastVisualPhase=-1;
    ctx.store.update('combat', d => { d.active = { id, state }; }, 'combat.turn');
    art.hit(state.beats.at(-1)!, ctx.clock.presentMs,state.winner);
    feedback.hit(state.beats.at(-1)!,ctx.clock.presentMs,state.winner,reduced);
    if (state.winner !== null) settle(ctx, id, state);
    refresh();
  }
  const keydown = (event: KeyboardEvent): void => {
    if (event.altKey || event.ctrlKey || event.metaKey || event.repeat || ctx.router.current?.id !== 'combat/fight') return;
    const action = actions[Number(event.key) - 1];
    if (action) { event.preventDefault(); act(action); }
  };
  void art.ready.then(()=>{if(!disposed)refresh();});
  document.addEventListener('keydown', keydown);
  if (state.winner !== null) settle(ctx, id, state);
  refresh();
  return {
    root, render(ms) {
      art.render(ms);
      feedback.render(ms);
      if (busyUntil) {
        const timing=duelTiming(state.beats.at(-1)!),elapsed=ms-actionAt;
        const phase=timing.heroContact.filter(at=>elapsed>=at).length+timing.enemyContacts.filter(at=>elapsed>=at).length*100;
        if(ms>=busyUntil){busyUntil=0;refresh();}else if(phase!==lastVisualPhase){lastVisualPhase=phase;refresh();}
      }
    },
    isIdle: () => ctx.clock.presentMs >= busyUntil,
    debugState: () => ({ id, state, live3d: art.live, art: art.debugState() }),
    dispose() { disposed = true; document.removeEventListener('keydown', keydown); feedback.dispose(); art.dispose(); disposeTree(root); },
  };
}

function instruction(ctx: ScreenContext): Screen {
  const root = shell(ctx, 'screen.instruction');
  root.append(h('ol', { class: 'duel-rules' }, ...[1, 2, 3, 4, 5].map(i => h('li', null, label(ctx, 'rule' + i)))),
    button(ctx, 'back', () => { void ctx.router.goto('combat/arena'); }, true));
  return simple(root);
}
function ladder(ctx: ScreenContext): Screen {
  const root = shell(ctx, 'screen.ladder'), saved = saveOf(ctx);
  root.append(h('h2', null, label(ctx, 'rank', { n: Math.min(12, saved.wins) })), h('p', null, label(ctx, 'rankHint')),
    h('div', { class: 'duel-ladder' }, ...Array.from({ length: 12 }, (_, i) => h('span', { class: 'duel-rung' + (i < saved.wins ? ' is-won' : '') }))),
    h('h2', null, label(ctx, 'history')));
  if (!saved.history.length) root.append(h('p', null, label(ctx, 'noHistory')));
  else root.append(h('ul', { class: 'duel-history' }, ...saved.history.slice(0, 8).map(row => h('li', null,
    h('span', null, label(ctx, 'foe.' + row.opponent)), h('b', null, label(ctx, row.won ? 'win' : 'loss'))))));
  root.append(button(ctx, 'back', () => { void ctx.router.goto('combat/arena'); }, true));
  return simple(root);
}
function result(ctx: ScreenContext): Screen {
  const active = saveOf(ctx).active;
  if (!active || active.state.winner === null) return arena(ctx);
  settle(ctx, active.id, active.state);
  const root = shell(ctx, 'screen.result'); root.append(resultCard(ctx, active.state));
  return simple(root);
}
function definition(id: ScreenDef['id'], title: string, create: ScreenDef['create'], options: Partial<ScreenDef> = {}): ScreenDef {
  return { id, titleKey: 'combat.screen.' + title, needs3d: false, kind: 'page', tab: 'arena',
    parseParams: q => Object.fromEntries(q.entries()), create, ...options };
}
export const combatScreens: ScreenDef[] = [
  definition('combat/arena', 'arena', arena, { route: '#/arena', aliases: ['arena'], kind: 'tabRoot' }),
  definition('combat/advice', 'advice', instruction, { kind: 'sheet' }),
  definition('combat/instruction', 'instruction', instruction, { kind: 'sheet' }),
  definition('combat/fight', 'fight', fight, { route: '#/arena/fight/:fightId', aliases: ['fight'], needs3d: true, kind: 'immersive' }),
  definition('combat/result', 'result', result, { route: '#/arena/result/:fightId' }),
  definition('combat/charter', 'charter', arena, { route: '#duel=:code' }),
  definition('combat/tournament', 'tournament', arena, { route: '#/arena/tournament', scope: 'P1' }),
  definition('combat/ladder', 'ladder', ladder, { route: '#/arena/ladder', aliases: ['ladder'], kind: 'sheet' }),
  definition('debug/balance', 'balance', ladder, { aliases: ['balance'], kind: 'debug' }),
];
